import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { GetContractsPlanLimit } from "../../../auth/planProvider/PlanProvider.hooks.ts";
import { DotLegalAlert, DotLegalFullScreenSpinner, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useAiSpendingWarning } from "../../../aiAdministration/AiAdministration.hooks.ts";
import { isAiSpendingExhausted } from "../../../aiAdministration/AiAdministration.types.ts";
import AiSpendingWarningAlert from "../../../aiAdministration/AiSpendingWarningAlert.tsx";
import TrialUsageWarning from "@common/components/TrialUsageWarning.tsx";
import { useCreateContractPane } from "./CreateContractPane.hooks.ts";
import { useCreateContractPaneStyles } from "./CreateContractPane.styles.ts";
import DotLegalFileUploadWrapper from "@common/components/dotLegalFileUpload/DotLegalFileUploadWrapper.tsx";
import UploadProgressBar from "@common/components/uploadProgress/UploadProgressBar";
import { EditableExtractionView } from "./EditableExtractionView.tsx";
import AiIcon from "./ai-icon.svg?react";
import AiFeatureGate from "../../../plan/aiFeatureGate/AiFeatureGate";
import { useAiFeatureAccess } from "../../../plan/aiFeatureGate/useAiFeatureAccess";
import ObligationTemplatesList from "../../components/obligationTemplatesList/ObligationTemplatesList.tsx";
import TemplateStepFields from "../../../entityTemplates/templateStepFields/TemplateStepFields.tsx";
import ContractTemplateField from "../../contractTemplateFields/ContractTemplateField.tsx";
import AIDialogDisclaimer from "@common/components/AIDialogDisclaimer.tsx";
import PaneLayout from "../../../main/panes/paneLayout/PaneLayout.tsx";
import { defineDocumentReviewPane } from "../../../main/panes/documentPane/documentReviewPane";
import { CreateContractPaneProps } from "./CreateContractPane.types.ts";

function CreateContractPane(props: CreateContractPaneProps) {
    const {
        createContract,
        inProgress,
        validation,
        contract,
        onChange,
        onFieldChange,
        onStatusChange,
        onContractDateChange,
        contractTemplates,
        contractTemplatesIsLoading,
        selectedTemplateId,
        isLoadingTemplate,
        creationMode,
        dialogStep,
        uploadedFile,
        extractionResult,
        extractContract,
        isExtracting,
        canExtract,
        isUploadedFileTooLargeForAi,
        createContractFromExtraction,
        canCreateFromExtraction,
        isCreatingFromExtraction,
        isUploadingExtractedDocument,
        uploadPercentage,
        editedContractParties,
        canEditStatus,
        canEditContractParties,
        removeContractParty,
        addContractPartyFromLegalEntity,
        selectableLegalEntities,
        selectableLegalEntitiesLoading,
        selectedObligationTemplates,
        setDialogStep,
        gotoObligationList,
        selectedObligations,
        onObligationsChange,
        showObligationErrors,
        onHasObligationValidationErrorsChange,
        manualFieldGroups,
        extractionFieldGroups,
        selectedTagIds,
        onTagsChange,
        requiredFieldValidationErrors,
        selectedTemplateMissingAccess,
        canShowDocument,
        showDocument,
        citationForField,
        showInDocument,
        isFromStoredDocument,
        extractedDocumentName,
    } = useCreateContractPane(props);
    const { translateString } = useTranslation();
    const { contractsPlanLimitData } = GetContractsPlanLimit();
    const styles = useCreateContractPaneStyles();
    const { showAffordance: showAiAffordance } = useAiFeatureAccess();
    const theme = useTheme();
    const { data: spendingWarning } = useAiSpendingWarning();
    const isAiExhausted = isAiSpendingExhausted(spendingWarning);

    const hasObligationTemplates = selectedObligationTemplates.length > 0;

    const getStepConfig = () => {
        if (creationMode === "manual") {
            if (dialogStep === "input") {
                return {
                    buttonText: hasObligationTemplates ? translateString("next") : translateString("create"),
                    onButtonClick: hasObligationTemplates ? gotoObligationList : createContract,
                };
            }
            return { buttonText: translateString("create"), onButtonClick: createContract };
        }

        if (dialogStep === "input") {
            return { buttonText: translateString("continue"), onButtonClick: extractContract };
        }
        return { buttonText: translateString("createContract"), onButtonClick: createContractFromExtraction };
    };

    const stepConfig = getStepConfig();

    const isPrimaryDisabled = () => {
        if (selectedTemplateMissingAccess) return true;
        // The required-at-creation fields now come from the template itself, so creating before it has
        // arrived would skip validating fields nobody has seen yet.
        if (dialogStep === "input" && isLoadingTemplate) return true;
        if (creationMode === "ai-assisted" && dialogStep === "input") {
            if (isAiExhausted) return true;
            return !canExtract;
        }
        if (creationMode === "ai-assisted" && dialogStep === "results") {
            return !canCreateFromExtraction;
        }
        return false;
    };

    const isAICreationWithResponse = creationMode === "ai-assisted" && dialogStep === "results" && extractionResult;

    return (
        <PaneLayout
            reserveFieldTooltipSpace
            header={translateString("createContract")}
            actions={
                canShowDocument ? (
                    <Tooltip title={translateString("showDocument")}>
                        <IconButton size="small" onClick={showDocument}>
                            <DescriptionOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : undefined
            }
            footerNote={creationMode === "ai-assisted" ? <AIDialogDisclaimer /> : undefined}
            okButtonText={stepConfig.buttonText}
            onOkClick={stepConfig.onButtonClick}
            okButtonDisabled={isPrimaryDisabled()}
            inProgress={inProgress || isExtracting || isCreatingFromExtraction}
            secondaryButtonText={dialogStep === "obligationTemplates" ? translateString("previous") : undefined}
            onSecondaryButtonClick={dialogStep === "obligationTemplates" ? () => setDialogStep("input") : undefined}
        >
            {dialogStep !== "obligationTemplates" && showAiAffordance && !isFromStoredDocument && (
                <Box sx={styles.toggleContainer}>
                    <Box sx={{ flex: 1 }}>
                        <Box
                            component="button"
                            sx={{
                                width: "100%",
                                height: "100%",
                                ...(creationMode === "manual" ? styles.toggleButtonActive : styles.toggleButton),
                            }}
                            onClick={() => onChange.onModeChange("manual")}
                        >
                            {translateString("manualCreation")}
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <AiFeatureGate>
                            {({ locked }) => (
                                <Box
                                    component="button"
                                    sx={{
                                        ...(locked
                                            ? styles.toggleButtonDisabled
                                            : creationMode === "ai-assisted"
                                              ? styles.toggleButtonActive
                                              : styles.toggleButton),
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        "& svg": {
                                            width: 16,
                                            height: 16,
                                            ml: 1,
                                            stroke: locked ? theme.palette.text.disabled : creationMode === "ai-assisted" ? "white" : "#7284FA",
                                        },
                                    }}
                                    onClick={locked ? undefined : () => onChange.onModeChange("ai-assisted")}
                                >
                                    {translateString("aiAssistedCreation")}
                                    <AiIcon />
                                </Box>
                            )}
                        </AiFeatureGate>
                    </Box>
                </Box>
            )}

            {dialogStep === "input" && (
                <DotLegalSelect
                    options={contractTemplates}
                    onChange={onChange.onTemplateChange}
                    label={translateString("contractTemplate")}
                    noOptionsLabel={translateString("noOptions")}
                    isLoading={contractTemplatesIsLoading}
                    toolTipText={translateString("addContractTemplateTooltip")}
                    disableClearable
                    selectedItem={selectedTemplateId}
                    errorText={validation?.getErrorsForDisplay("entityTemplateId")}
                    disabled={creationMode === "ai-assisted" && isAiExhausted}
                />
            )}

            {dialogStep === "input" && selectedTemplateMissingAccess && (
                <Box sx={{ marginTop: 2 }}>
                    <DotLegalAlert severity="info" warningText={translateString("missingRequiredAtCreationFieldAccess")} />
                </Box>
            )}

            {creationMode === "manual" && dialogStep === "input" && (
                <>
                    {isLoadingTemplate && <DotLegalFullScreenSpinner />}
                    {!selectedTemplateMissingAccess && selectedTemplateId && (
                        <TemplateStepFields
                            groups={manualFieldGroups}
                            renderField={(field) => (
                                <ContractTemplateField
                                    field={field}
                                    contract={contract}
                                    onChange={onFieldChange}
                                    validationErrors={requiredFieldValidationErrors}
                                    selectedTagIds={selectedTagIds}
                                    onTagsChange={onTagsChange}
                                    autoFocusName
                                />
                            )}
                        />
                    )}
                </>
            )}

            {creationMode === "manual" && dialogStep === "obligationTemplates" && (
                <ObligationTemplatesList
                    obligationTemplates={selectedObligationTemplates}
                    initialSelectedObligations={selectedObligations}
                    onChange={onObligationsChange}
                    contractStartDate={contract.startDate}
                    contractEndDate={contract.endDate}
                    onContractStartDateChange={(date) => onContractDateChange("startDate", date ?? undefined)}
                    onContractEndDateChange={(date) => onContractDateChange("endDate", date ?? undefined)}
                    contractEndDateError={validation?.getErrorsForDisplay("endDate")}
                    showErrors={showObligationErrors}
                    onValidationChange={onHasObligationValidationErrorsChange}
                />
            )}

            {creationMode === "ai-assisted" && dialogStep === "input" && (
                <>
                    {!isFromStoredDocument && (
                        <Box sx={{ marginTop: 2 }}>
                            <DotLegalFileUploadWrapper
                                files={uploadedFile ? [uploadedFile.fileName] : []}
                                onFilesChanged={onChange.onFileUploadChange}
                                isLoading={false}
                                disabled={isAiExhausted}
                            />
                        </Box>
                    )}
                    {isUploadedFileTooLargeForAi && (
                        <Box sx={{ marginTop: 2 }}>
                            <DotLegalAlert severity="warning" warningText={translateString("textExtractionErrorFileTooLarge")} />
                        </Box>
                    )}
                    {isExtracting && (
                        <Box sx={{ marginTop: 2 }}>
                            <DotLegalAlert
                                severity="info"
                                warningHeader={translateString("extractingContractHeader")}
                                warningText={translateString("extractingContractInfo")}
                            />
                        </Box>
                    )}
                    <AiSpendingWarningAlert warning={spendingWarning} />
                </>
            )}

            {isAICreationWithResponse && (
                <>
                    <EditableExtractionView
                        documentName={extractedDocumentName}
                        extractionResult={extractionResult}
                        contract={contract}
                        fieldGroups={extractionFieldGroups}
                        citationForField={citationForField}
                        onShowInDocument={showInDocument}
                        isLoadingTemplate={isLoadingTemplate}
                        onFieldChange={onFieldChange}
                        onStatusChange={onStatusChange}
                        editedContractParties={editedContractParties}
                        canEditStatus={canEditStatus}
                        canEditContractParties={canEditContractParties}
                        onRemoveParty={removeContractParty}
                        onAddParty={addContractPartyFromLegalEntity}
                        selectableLegalEntities={selectableLegalEntities}
                        selectableLegalEntitiesLoading={selectableLegalEntitiesLoading}
                        validationErrors={requiredFieldValidationErrors}
                        selectedTagIds={selectedTagIds}
                        onTagsChange={onTagsChange}
                    />
                    <UploadProgressBar isUploading={isUploadingExtractedDocument} uploadPercentage={uploadPercentage} />
                </>
            )}

            {creationMode === "manual" && contractsPlanLimitData?.warnLimitReachedOnTrial && (
                <TrialUsageWarning textKey={"createExceedingNumberOfContractWarning"} marginTop={3} />
            )}
        </PaneLayout>
    );
}

// A review of the stored document it reads, so paging the document pane to another document closes it. An upload
// is not reviewed in that sense: it has no id the documents on the stage could share.
export default defineDocumentReviewPane({
    slot: "right",
    component: CreateContractPane,
    reviewedDocumentId: (props) => props.sourceDocument?.document.documentId,
});
